import { memo } from 'react';
import { Link } from 'react-router-dom';
import { zh } from '../i18n';
import { costLineParts } from '../lib/cost';
import { basename, formatUsd } from '../lib/format';
import type { ToolItem, TurnState } from '../stream/streamReducer';
import { activityCounts } from './grouping';

/**
 * Per-turn footer: 活動摘要 strip · why the turn stopped · cost line ·
 * 記憶足跡 chips (SPEC §10, §11).
 *
 * The cost line delegates to `costLineParts`, the only place allowed to decide
 * whether dollars exist — a `$0.00` printed under a subscription credential
 * would be a fabricated number (CONTRACTS §4.1).
 *
 * The stop reason is rendered from the turn's own `resultSubtype`, with the
 * employee's configured ceilings substituted in, because SPEC §11 freezes those
 * two sentences with the actual limit in them: a user told "已達花費上限" wants
 * to know which number they hit.
 */
export interface TurnFooterProps {
  readonly turn: TurnState;
  readonly tools: readonly ToolItem[];
  readonly maxTurns: number | null;
  readonly maxBudgetUsd: number | null;
  readonly budgetsEnforced: boolean;
}

function stopReason(
  turn: TurnState,
  maxTurns: number | null,
  maxBudgetUsd: number | null,
  budgetsEnforced: boolean,
): string | null {
  if (turn.resultSubtype === 'error_max_turns') {
    return maxTurns === null ? zh.chat.turnFailed : zh.chat.resultMaxTurnsDetail(maxTurns);
  }
  // The dollar half of SPEC §11 only ever fires under an api_key credential.
  if (turn.resultSubtype === 'error_max_budget_usd' && budgetsEnforced && maxBudgetUsd !== null) {
    return zh.chat.resultMaxBudget(formatUsd(maxBudgetUsd));
  }
  if (turn.error) return turn.error.message;
  if (turn.status === 'interrupted') {
    const reason = turn.interruptedReason;
    const known =
      reason && reason in zh.chat.interruptReason
        ? zh.chat.interruptReason[reason as keyof typeof zh.chat.interruptReason]
        : null;
    return known ?? zh.chat.interrupted(reason ?? '');
  }
  if (turn.status === 'failed') return zh.chat.turnFailed;
  return null;
}

/**
 * Memoized on identity for the same reason `ToolCard` is: the tool array is
 * rebuilt on every stream tick, and a settled turn's footer never changes.
 */
export const TurnFooter = memo(
  function TurnFooter({
    turn,
    tools,
    maxTurns,
    maxBudgetUsd,
    budgetsEnforced,
  }: TurnFooterProps): JSX.Element | null {
    const counts = activityCounts(tools);
    const usage = turn.usage;
    const workspace = turn.workspace;

    const chips: { key: string; label: string; path: string }[] = [];
    if (workspace) {
      for (const path of workspace.created) {
        chips.push({ key: `c:${path}`, label: zh.memory.footprintCreated(basename(path)), path });
      }
      for (const path of workspace.modified) {
        chips.push({ key: `m:${path}`, label: zh.memory.footprintModified(basename(path)), path });
      }
      for (const path of workspace.deleted) {
        chips.push({ key: `d:${path}`, label: zh.memory.footprintDeleted(basename(path)), path });
      }
    }

    const showActivity = counts.total > 0;
    const note = stopReason(turn, maxTurns, maxBudgetUsd, budgetsEnforced);

    if (!showActivity && !usage && chips.length === 0 && !note) return null;

    return (
      <div className="turn-foot">
        {showActivity ? (
          <div className="activity">
            <span>{zh.tool.activityTitle}</span>
            {counts.read > 0 ? <span>{`${zh.tool.activityRead} ${counts.read}`}</span> : null}
            {counts.write > 0 ? <span>{`${zh.tool.activityWrite} ${counts.write}`}</span> : null}
            {counts.command > 0 ? (
              <span>{`${zh.tool.activityCommand} ${counts.command}`}</span>
            ) : null}
            {counts.other > 0 ? <span>{`${zh.tool.activityOther} ${counts.other}`}</span> : null}
          </div>
        ) : null}

        {note ? <div className="small muted">{note}</div> : null}

        {usage ? (
          <div className="cost-line">
            {costLineParts({
              costUsd: usage.costUsd,
              costAvailable: usage.costAvailable,
              numTurns: usage.numTurns,
              inputTokens: usage.inputTokens,
              outputTokens: usage.outputTokens,
              cacheReadTokens: usage.cacheReadTokens,
              durationMs: turn.durationMs,
            }).map((part, index) => (
              <span key={`${part}-${index}`}>{part}</span>
            ))}
          </div>
        ) : null}

        {chips.length > 0 ? (
          <div className="chips" aria-label={zh.memory.footprintTitle}>
            {chips.map((chip) => (
              <Link
                key={chip.key}
                className="chip"
                title={zh.memory.openInMemory}
                to={`/memory?path=${encodeURIComponent(chip.path)}`}
              >
                {chip.label}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
  (prev, next) =>
    prev.turn === next.turn &&
    prev.maxTurns === next.maxTurns &&
    prev.maxBudgetUsd === next.maxBudgetUsd &&
    prev.budgetsEnforced === next.budgetsEnforced &&
    prev.tools.length === next.tools.length &&
    prev.tools.every((tool, index) => tool === next.tools[index]),
);
